import { BarDatum, ResponsiveBar } from '@nivo/bar';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { addAlpha } from 'libs/end-to-end/src/progress-page-utils';
import { roundAxisValue } from 'libs/end-to-end/src/progress-page-utils';
import {
  BarItemProps,
  BarLayer,
  ComputedBarDatum,
  ComputedDatum,
} from '@nivo/bar/dist/types/types';
import { KARLA_FONT_FAMILY } from 'libs/end-to-end/src/types/branding/fonts';
import { AxisTick } from 'apps/sigma2-ui/src/components/AxisTick';
import { AxisTickProps } from '@nivo/axes/dist/types/types';
import { CartesianMarkerProps, patternLinesDef } from '@nivo/core';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { Colors } from 'libs/end-to-end/src/types/branding/color';
import { S2ChartBar } from 'apps/sigma2-ui/src/components/S2ChartBar';
import { ConfigColors } from 'libs/end-to-end/src/types/branding/colors';
import { AxisProps } from '@nivo/axes';
import useMeasure from 'react-use-measure';
import { clamp } from 'lodash';
import { S2GroupedBarChartGroupLevelSerializable } from 'libs/end-to-end/src/grouped-bar';
import { S2GroupedBarChartSerializableProps } from 'libs/end-to-end/src/grouped-bar';
import { S2GroupedBarChartGroupSerializable } from 'libs/end-to-end/src/grouped-bar';
import { S2GroupedBarChartKey } from 'libs/end-to-end/src/grouped-bar';
import { formatNumberForDisplayInUnits } from 'libs/end-to-end/src/number-formatting';
import { Datum } from '@nivo/line';

export interface S2GroupedBarChartGroupLevel
  extends S2GroupedBarChartGroupLevelSerializable {
  tooltip?: React.ReactNode;
  isCheckered?: boolean;
}

export interface S2GroupedBarChartGroup
  extends S2GroupedBarChartGroupSerializable {
  groupLevels: S2GroupedBarChartGroupLevel[];
}

const CHARACTER_APPROXIMATE_WIDTH_PX = 5.8;

export interface S2GroupedBarChartProps
  extends S2GroupedBarChartSerializableProps {
  xGroups: S2GroupedBarChartGroup[];
  layers?: BarLayer<BarDatum>[];
  displayHorizontally?: boolean;
  barComponent?: React.ComponentType;
  onBarClick?: (barProps: BarItemProps<BarDatum>) => void;
  getExtraMarginLeft?: (maxYLength: number) => number;
  getExtraMarginRight?: (maxXLength: number) => number;
  getExtraMarginBottom?: (xAxisLabel: string | null | undefined) => number;
  colorsCb?: (bar: ComputedDatum<BarDatum>) => string;
  axisBottom?: AxisProps | null;
  axisLeft?: AxisProps | null;
}

function reduceGroupLevel(
  currentValue: {
    [key: string]: number | string;
  },
  groupLevel: S2GroupedBarChartGroupLevel,
  color: Color,
  units: string,
) {
  return {
    ...currentValue,
    [groupLevel.id]: groupLevel.value,
    [`${groupLevel.id}Color`]: ConfigColors[color].Primary.raw,
    [`${groupLevel.id}_GroupCheckered`]: groupLevel.isCheckered
      ? 'true'
      : 'false',
    [`${groupLevel.id}_Units`]: units,
  };
}

const createAxisLeftTick = (maxLabelWidthPx?: number) => {
  const AxisLeftTick = (props: AxisTickProps<number>) => {
    const textStyle =
      props.value === 0 ? { fill: '#292D33', fontWeight: 700 } : {};

    return (
      <AxisTick
        {...props}
        textStyle={textStyle}
        maxLabelWidthPx={maxLabelWidthPx}
      />
    );
  };

  AxisLeftTick.displayName = 'AxisLeftTick';

  return AxisLeftTick;
};

const createAxisBottomTick = (maxLabelWidthPx?: number) => {
  const AxisBottomTick = (props: AxisTickProps<string | number>) => (
    <AxisTick
      {...props}
      textStyle={{ fill: '#292D33' }}
      maxLabelWidthPx={maxLabelWidthPx}
    />
  );

  AxisBottomTick.displayName = 'AxisBottomTick';
  return AxisBottomTick;
};

const chartMarkers: CartesianMarkerProps[] = [
  {
    axis: 'y',
    value: 0,
    lineStyle: {
      stroke: '#292D33',
      strokeWidth: '1px',
    },
  },
];

const defaultTheme = {
  axis: {
    ticks: {
      text: {
        fill: '#696D70',
        fontFamily: KARLA_FONT_FAMILY,
        fontSize: 12,
      },
    },
    legend: {
      text: {
        fontFamily: KARLA_FONT_FAMILY,
        fontWeight: '700',
      },
    },
  },
};

const barAriaLabel = ({ id, indexValue }: ComputedDatum<BarDatum>) =>
  `${id}-${indexValue}-bar`;

/**
 * A noop function that returns an empty fragment. This is used to override
 * nivo's default tooltip.
 */
const noop = () => <></>;

function calculateBarWidth(
  containerWidth: number,
  chartMargin: { left: number; right: number },
  groupCount: number,
  barsPerGroup: number,
  padding: number = 0.3,
  innerPadding: number = 12,
): number {
  // Calculate available width by subtracting margins
  const availableWidth = containerWidth - chartMargin.left - chartMargin.right;

  // Calculate the width allocated to each group
  const groupWidth = (availableWidth * (1 - padding)) / groupCount;

  // Calculate the width of each bar within a group
  const barWidth =
    (groupWidth - innerPadding * (barsPerGroup - 1)) / barsPerGroup;

  return barWidth;
}

export const S2GroupedBarChart = ({
  xGroups,
  xLevels,
  yAxisLabel,
  xAxisLabel,
  units,
  horizontalFixedHeight,
  horizontalMinHeight,
  horizontalMaxHeight,
  horizontalHeightAsFactorOfWidth,
  horizontalHeight,
  horizontalMinBarWidth,
  horizontalGroupPadding,
  verticalBarFixedThickness,
  verticalBarThickness,
  verticalBarThicknessAsFactorOfWidth,
  verticalBarMinThickness,
  verticalBarMaxThickness,
  verticalMinHeight,
  verticalMaxHeight,
  verticalGroupPaddingAsFactorOfHeight,
  verticalInnerPadding,
  margin,
  layers,
  hoverEffect = true,
  displayHorizontally = false,
  enableGridX,
  enableGridY,
  enableAxisX = true,
  enableAxisY = true,
  fills,
  defs,
  markers,
  theme,
  barComponent,
  onBarClick,
  colorsCb: colorsCbProp,
  axisBottom: axisBottomProp,
  axisLeft: axisLeftProp,
  maxYAxisLabelWidthPx,
  ...props
}: S2GroupedBarChartProps) => {
  const keys = useMemo(() => {
    const isGroupLevelCheckered = xGroups.some((xGroup) =>
      xGroup.groupLevels.some((xLevel) => xLevel.isCheckered === true),
    );

    const finalList: string[] = [];
    xLevels.forEach((xLevel) => {
      const id =
        typeof xLevel.id === 'number' ? `${xLevel.id.toString(10)}` : xLevel.id;
      finalList.push(id);

      if (isGroupLevelCheckered) {
        finalList.push(`${id}_Checkered`);
      }
    });

    return finalList;
  }, [xLevels, xGroups]);

  const getColorForLevel = useCallback(
    (levelId: S2GroupedBarChartKey) => {
      // Create a map of assigned colors
      const assignedColors = new Map<S2GroupedBarChartKey, Color>();

      // Track used colors
      const usedColors = new Set<Color>();

      for (const xLevel of xLevels) {
        if (xLevel.color) {
          usedColors.add(xLevel.color);
        }
      }

      // Assign colors to levels
      for (const xLevel of xLevels) {
        if (xLevel.color) {
          // If color is defined, use it
          assignedColors.set(xLevel.id, xLevel.color);
          usedColors.add(xLevel.color);
        } else {
          // Find the first unused color
          const unusedColor = Colors.find((color) => !usedColors.has(color));
          if (unusedColor) {
            assignedColors.set(xLevel.id, unusedColor);
            usedColors.add(unusedColor);
          } else {
            // If no unused colors are available, cycle through the Colors array
            const cycleColor = Colors[assignedColors.size % Colors.length];
            assignedColors.set(xLevel.id, cycleColor);
          }
        }
      }

      // Return the color for the given level ID
      return assignedColors.get(levelId) ?? 'Gray';
    },
    [xLevels],
  );

  // store hovered bar's level. when no bar is hovered it will be null
  const [hoveredLevel, setHoveredLevel] = useState<
    [S2GroupedBarChartKey, S2GroupedBarChartKey] | null
  >(null);

  const [selectedLevel, setSelectedLevel] = useState<
    [S2GroupedBarChartKey, S2GroupedBarChartKey] | null
  >(null);

  // colors callback to fill bars. when one bar is hovered others will have an
  // opacity of 0.2
  const colorsCb = useCallback(
    (bar: ComputedDatum<BarDatum>) => {
      const { id, data } = bar;
      if (colorsCbProp) {
        return colorsCbProp(bar);
      }

      const color = String(data[`${id}Color`]);

      return hoveredLevel === null || hoveredLevel[0] === id || !hoverEffect
        ? color
        : addAlpha(color, 0.2);
    },
    [hoverEffect, hoveredLevel, colorsCbProp],
  );

  const computedDefs = useMemo(() => {
    if (defs) {
      return defs;
    }

    const colors: Color[] = [];
    xLevels.forEach((xLevel) => {
      if (xLevel.color) {
        colors.push(xLevel.color);
      }
    });

    return colors.map((color) => {
      return patternLinesDef(`${color}_DefCheckered`, {
        spacing: 5,
        rotation: -45,
        lineWidth: 2,
        background: 'transparent',
        color: ConfigColors[color].Primary.raw,
      });
    });
  }, [xLevels, defs]);

  // map received data to a format supported by ResponsiveBar
  const chartData = useMemo(() => {
    return xGroups.map((xGroup) =>
      xGroup.groupLevels.reduce(
        (currentValue, groupLevel) => {
          return reduceGroupLevel(
            currentValue,
            groupLevel,
            getColorForLevel(groupLevel.id),
            units,
          );
        },
        { groupId: xGroup.id },
      ),
    );
  }, [xGroups, getColorForLevel, units]);

  const computedFills = useMemo(() => {
    if (fills) {
      return fills;
    }

    if (defs) {
      return [
        {
          match: (bar: ComputedBarDatum<BarDatum>) => {
            return (
              bar.data.id === selectedLevel?.[0] &&
              bar.data.data.groupId === selectedLevel?.[1]
            );
          },
          id: cleanDefId(`${selectedLevel?.[0]}${selectedLevel?.[1]}_Selected`),
        },
        {
          match: (bar: ComputedBarDatum<BarDatum>) => {
            return (
              bar.data.id === hoveredLevel?.[0] &&
              bar.data.data.groupId === hoveredLevel?.[1]
            );
          },
          id: cleanDefId(`${hoveredLevel?.[0]}${hoveredLevel?.[1]}_Hover`),
        },
        ...xGroups.flatMap((xGroup) =>
          xGroup.groupLevels.map((xLevel) => ({
            match: (bar: ComputedBarDatum<BarDatum>) => {
              return (
                bar.data.id === xLevel.id && bar.data.indexValue === xGroup.id
              );
            },
            id: cleanDefId(`${xLevel.id}${xGroup.id}`),
          })),
        ),
      ];
    }

    const localFills: { match: Record<string, unknown>; id: string }[] = [];
    xLevels.forEach((xLevel) => {
      if (xLevel.color) {
        localFills.push({
          match: { id: `${xLevel.id}_Checkered` },
          id: `${xLevel.color}_DefCheckered`,
        });
      }
    });

    return localFills;
  }, [fills, defs, xLevels, hoveredLevel, xGroups, selectedLevel]);

  // memoize computed max value for the vertical axis of the chart. Multiply
  // this value so there is some margin above bars
  const [minVisualYValue, maxVisualYValue] = useMemo(() => {
    const values = xGroups.flatMap((xGroup) => {
      return xGroup.groupLevels.map(({ value }) => value);
    });
    const anyNegative = values.some((value) => value < 0);
    const anyPositive = values.some((value) => value > 0);
    const minValue = anyNegative ? Math.min(...values) : 0;
    const maxValue = anyPositive ? Math.max(...values) : 0;
    return [minValue * 1.2, maxValue * 1.2];
  }, [xGroups]);

  const formatYAxis = useCallback(
    (value: number) => {
      return formatNumberForDisplayInUnits({
        value,
        // For some reason, these percentages are pre-multiplied by 100.
        isPercentageAlreadyMultipliedBy100: true,
        units,
      });
    },
    [units],
  );

  // Calculate what values to display on the vertical axis. this list should
  // have nice rounded values so the chart is easier to read
  const tickValues = useMemo(() => {
    const numTicks = 5;

    // 1. Calculate the range (ie: 10)
    const range = maxVisualYValue - minVisualYValue;

    // 2. Calculate an unrounded interval (ie: 2.5)
    const roughInterval = range / (numTicks - 1);

    // 3. Round the interval to a "nice" number (ie: 2)
    const niceInterval = roundAxisValue(roughInterval);

    // 4. Generate the tick values
    const niceMin = Math.floor(minVisualYValue / niceInterval) * niceInterval;
    const niceMax = Math.ceil(maxVisualYValue / niceInterval) * niceInterval;

    const ticks: number[] = [];
    for (let tick = niceMin; tick <= niceMax; tick += niceInterval) {
      ticks.push(parseFloat(tick.toFixed(2)));
    }
    if (ticks.length === 0) {
      ticks.push(Math.floor(minVisualYValue), Math.ceil(maxVisualYValue));
    }
    return ticks;
  }, [minVisualYValue, maxVisualYValue]);

  const numOfBars = useMemo(() => {
    return xGroups.flatMap((xGroup) => xGroup.groupLevels).length;
  }, [xGroups]);

  const longestGroupLabel = useMemo(() => {
    const groupLabelLengths = xGroups.map((group) => group.label.length);
    return Math.max(...groupLabelLengths);
  }, [xGroups]);

  const [measureRef, measurements] = useMeasure();

  const isBellowMinWidth = useMemo(() => {
    if (measurements.width && horizontalMinBarWidth) {
      const barWidth = calculateBarWidth(
        measurements.width,
        { left: 65, right: 20 },
        xGroups.length,
        xLevels.length,
        0.3,
        12,
      );

      const longestGroupLabelWidth =
        longestGroupLabel * CHARACTER_APPROXIMATE_WIDTH_PX;

      return (
        barWidth < horizontalMinBarWidth ||
        longestGroupLabelWidth > barWidth * xLevels.length
      );
    }

    return false;
  }, [
    measurements.width,
    xGroups,
    horizontalMinBarWidth,
    xLevels.length,
    longestGroupLabel,
  ]);

  const shouldDisplayVertically = useMemo(() => {
    return isBellowMinWidth && !displayHorizontally;
  }, [isBellowMinWidth, displayHorizontally]);

  const formatAxisLegend = useCallback(
    (value: string | undefined, isYAxis: boolean) => {
      if (shouldDisplayVertically === isYAxis || value === undefined) {
        return value;
      }

      switch (units) {
        case 'percentage':
          return `${value} (%)`;
        case 'count':
          return `${value}`;
        case 'dollars':
          return `${value} ($)`;
      }
    },
    [shouldDisplayVertically, units],
  );

  // Calculate extra padding for the left side of the chart. This is needed to
  // make sure the vertical axis labels are not cut off when Y axis values are
  // large.
  const maxYLength = useMemo(() => {
    return shouldDisplayVertically
      ? longestGroupLabel
      : formatYAxis(maxVisualYValue).length;
  }, [
    formatYAxis,
    longestGroupLabel,
    maxVisualYValue,
    shouldDisplayVertically,
  ]);

  const maxXLength = useMemo(() => {
    return shouldDisplayVertically
      ? formatYAxis(maxVisualYValue).length
      : longestGroupLabel;
  }, [
    formatYAxis,
    longestGroupLabel,
    maxVisualYValue,
    shouldDisplayVertically,
  ]);

  // callback to render a bar. This function wraps the bar in a tooltip.
  const barCallback = useCallback(
    (props: BarItemProps<BarDatum>) => {
      const { id: barLevelId, indexValue: barGroupId } = props.bar.data;

      const xGroup = xGroups.find((g) => g.id === barGroupId) ?? null;

      if (!xGroup) {
        return null;
      }

      const groupLevel =
        xGroup.groupLevels.find((l) => l.id === barLevelId) ?? null;

      if (!groupLevel) {
        return null;
      }

      const BarComponent = barComponent ?? S2ChartBar;

      return (
        <BarComponent
          barProps={props}
          onMouseEnter={() => {
            setHoveredLevel([barLevelId, barGroupId]);
          }}
          onMouseLeave={() => {
            setHoveredLevel(null);
          }}
          onClick={(barProps: BarItemProps<BarDatum>) => {
            onBarClick?.(barProps);
            setSelectedLevel([barLevelId, barGroupId]);
          }}
          tooltip={groupLevel.tooltip}
          shouldDisplayVertically={shouldDisplayVertically}
          hoverEffect={hoverEffect}
        />
      );
    },
    [xGroups, shouldDisplayVertically, hoverEffect, barComponent, onBarClick],
  );

  const getBorderColor = useCallback(
    (bar: Datum) => {
      if (
        bar.data.id === selectedLevel?.[0] &&
        bar.data.data.groupId === selectedLevel?.[1]
      ) {
        return '#537AEE';
      }

      return 'transparent';
    },
    [selectedLevel],
  );

  // calculate extra padding to accommodate the longest Y axis label
  const extraPaddingYAxis = useMemo(() => 10 * (maxYLength / 2), [maxYLength]);

  const getExtraMarginLeft = useCallback(() => {
    if (props.getExtraMarginLeft) {
      return props.getExtraMarginLeft(maxYLength);
    }

    return 0;
  }, [maxYLength, props]);

  const getExtraMarginRight = useCallback(() => {
    if (props.getExtraMarginRight) {
      return props.getExtraMarginRight(maxXLength);
    }

    return 0;
  }, [maxXLength, props]);

  const getExtraMarginBottom = useCallback(() => {
    if (props.getExtraMarginBottom) {
      return props.getExtraMarginBottom(xAxisLabel);
    }

    return 0;
  }, [xAxisLabel, props]);

  const chartMargin = useMemo(
    () => ({
      top: margin?.top ?? 50,
      right: (margin?.right ?? 20) + getExtraMarginRight(),
      bottom: (margin?.bottom ?? 24) + getExtraMarginBottom(),
      left: (margin?.left ?? 65) + getExtraMarginLeft(),
    }),

    [
      margin?.top,
      margin?.right,
      margin?.bottom,
      margin?.left,
      getExtraMarginRight,
      getExtraMarginBottom,
      getExtraMarginLeft,
    ],
  );

  const computedMaxYAxisLabelWidthPx = useMemo(() => {
    if (maxYAxisLabelWidthPx !== undefined) {
      return maxYAxisLabelWidthPx;
    }

    return Math.max(0, chartMargin.left - 28);
  }, [maxYAxisLabelWidthPx, chartMargin.left]);

  const computedMaxXAxisLabelWidthPx = useMemo(() => {
    if (!measurements.width || shouldDisplayVertically) {
      return undefined;
    }

    const availableWidth =
      measurements.width - chartMargin.left - chartMargin.right;

    const groupWidth =
      (availableWidth * (1 - (horizontalGroupPadding ?? 0.3))) / xGroups.length;

    return Math.max(0, groupWidth - 8);
  }, [
    measurements.width,
    chartMargin.left,
    chartMargin.right,
    xGroups.length,
    horizontalGroupPadding,
    shouldDisplayVertically,
  ]);

  const horizontalStyleProps = useMemo(() => {
    const aspectRatio = horizontalFixedHeight
      ? undefined
      : `1 / ${horizontalHeightAsFactorOfWidth ?? 0.565}`;

    return {
      aspectRatio,
      minHeight:
        !horizontalFixedHeight &&
        horizontalMinHeight !== undefined &&
        horizontalMinHeight !== 0
          ? `${horizontalMinHeight}px`
          : undefined,
      maxHeight:
        !horizontalFixedHeight &&
        horizontalMaxHeight !== undefined &&
        horizontalMaxHeight !== 0
          ? `${horizontalMaxHeight}px`
          : undefined,
      height: horizontalFixedHeight ? `${horizontalHeight}px` : '100%',
      width: '100%',
      overflow: 'auto',
      position: 'relative' as const,
    };
  }, [
    horizontalFixedHeight,
    horizontalHeight,
    horizontalHeightAsFactorOfWidth,
    horizontalMaxHeight,
    horizontalMinHeight,
  ]);

  // Calculate the thickness of the bars when displayed vertically
  const verticalBarThicknessPx = useMemo(() => {
    // Default to 0 or verticalBarMinThickness if it is set
    let localVerticalBarThickness =
      verticalBarMinThickness && verticalBarMaxThickness
        ? Number((verticalBarMinThickness + verticalBarMaxThickness) / 2)
        : 0;

    // Use the fixed thickness if it is set
    if (verticalBarFixedThickness && verticalBarThickness !== undefined) {
      localVerticalBarThickness = verticalBarThickness;
    }

    // Calculate the thickness as a factor of the container width if it is set
    if (!verticalBarFixedThickness && verticalBarThicknessAsFactorOfWidth) {
      localVerticalBarThickness =
        measurements.width * verticalBarThicknessAsFactorOfWidth;
    }

    // clamp value to min and max thickness provided from arguments
    if (
      verticalBarMaxThickness &&
      localVerticalBarThickness > verticalBarMaxThickness
    ) {
      return verticalBarMaxThickness;
    }

    if (
      verticalBarMinThickness &&
      localVerticalBarThickness < verticalBarMinThickness
    ) {
      return verticalBarMinThickness;
    }

    return localVerticalBarThickness;
  }, [
    measurements.width,
    verticalBarFixedThickness,
    verticalBarMaxThickness,
    verticalBarMinThickness,
    verticalBarThickness,
    verticalBarThicknessAsFactorOfWidth,
  ]);

  // Calculate the padding between groups of bars when displayed vertically
  // in Px
  const verticalAllBarsPadding = useMemo(() => {
    if (!verticalBarFixedThickness && verticalGroupPaddingAsFactorOfHeight) {
      const verticalPaddingSpaces = xGroups.length + 2;

      return Math.round(
        verticalBarThicknessPx *
          verticalGroupPaddingAsFactorOfHeight *
          verticalPaddingSpaces,
      );
    }

    return 0;
  }, [
    verticalBarFixedThickness,
    verticalBarThicknessPx,
    verticalGroupPaddingAsFactorOfHeight,
    xGroups.length,
  ]);

  const leftAxisLegendApproximateHeight = 12 * yAxisLabel.length;

  // Calculate the height of the chart when displayed vertically
  const getVerticalHeight = useCallback(() => {
    const allBarsTotalHeight = numOfBars * verticalBarThicknessPx;

    const verticalChartHeight =
      allBarsTotalHeight +
      chartMargin.top +
      chartMargin.bottom +
      verticalAllBarsPadding +
      (verticalInnerPadding || 0);

    if (verticalMinHeight && verticalMaxHeight) {
      return clamp(verticalChartHeight, verticalMinHeight, verticalMaxHeight);
    }

    return verticalChartHeight;
  }, [
    numOfBars,
    verticalBarThicknessPx,
    chartMargin.top,
    chartMargin.bottom,
    verticalAllBarsPadding,
    verticalInnerPadding,
    verticalMinHeight,
    verticalMaxHeight,
  ]);

  const verticalStyleProps = useMemo<CSSProperties>(() => {
    const height = Math.max(
      getVerticalHeight(),
      leftAxisLegendApproximateHeight,
    );
    return {
      height: `${height}px`,
      width: '100%',
      position: 'relative',
    };
  }, [getVerticalHeight, leftAxisLegendApproximateHeight]);

  const styleProps = useMemo(() => {
    return shouldDisplayVertically ? verticalStyleProps : horizontalStyleProps;
  }, [horizontalStyleProps, shouldDisplayVertically, verticalStyleProps]);

  // Any time the key changes we force a re-render.
  const [chartKey, setChartKey] = useState(0);

  const forceRerender = useCallback(() => {
    setChartKey((prevKey) => prevKey + 1);
  }, []);

  const axisLeftTickRenderer = useMemo(
    () => createAxisLeftTick(computedMaxYAxisLabelWidthPx),
    [computedMaxYAxisLabelWidthPx],
  );

  const axisBottomTickRenderer = useMemo(
    () => createAxisBottomTick(computedMaxXAxisLabelWidthPx),
    [computedMaxXAxisLabelWidthPx],
  );

  const axisBottom = useMemo<AxisProps | null>(() => {
    const correctAxisFlag = shouldDisplayVertically ? enableAxisY : enableAxisX;

    if (!correctAxisFlag) {
      return null;
    }

    return {
      tickSize: 0,
      tickPadding: 12,
      tickRotation: 0,
      truncateTickAt: 0,
      legend: xAxisLabel ? formatAxisLegend(xAxisLabel, false) : '',
      legendPosition: 'middle',
      legendOffset: 43,
      renderTick: axisBottomTickRenderer,
      ...(shouldDisplayVertically
        ? {
            tickValues: tickValues,
            format: formatYAxis,
          }
        : {}),
      ...(axisBottomProp ?? {}),
    };
  }, [
    shouldDisplayVertically,
    enableAxisY,
    enableAxisX,
    xAxisLabel,
    formatAxisLegend,
    tickValues,
    formatYAxis,
    axisBottomProp,
    axisBottomTickRenderer,
  ]);

  const axisLeft = useMemo<AxisProps | null>(() => {
    const correctAxisFlag = shouldDisplayVertically ? enableAxisX : enableAxisY;

    if (!correctAxisFlag) {
      return null;
    }

    return {
      tickSize: 0,
      tickPadding: 10,
      tickRotation: 0,
      legend: yAxisLabel.length > 0 ? formatAxisLegend(yAxisLabel, true) : '',
      legendPosition: 'middle',
      legendOffset: -55 - extraPaddingYAxis,
      truncateTickAt: 0,
      renderTick: axisLeftTickRenderer,
      ...(shouldDisplayVertically
        ? {}
        : {
            tickValues: tickValues,
            format: formatYAxis,
          }),
      ...(axisLeftProp ?? {}),
    };
  }, [
    shouldDisplayVertically,
    enableAxisX,
    enableAxisY,
    yAxisLabel,
    formatAxisLegend,
    extraPaddingYAxis,
    tickValues,
    formatYAxis,
    axisLeftProp,
    axisLeftTickRenderer,
  ]);

  // Force a re-render if the window resizes.
  useEffect(() => {
    const handleResize = () => {
      forceRerender();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [forceRerender]);

  // Force a re-render if the styleProps change.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    forceRerender();
  }, [forceRerender, styleProps]);

  const gridYValues = useMemo(
    () => xGroups.map(({ id }) => String(id)),
    [xGroups],
  );

  const chartWidth = useMemo(() => {
    if (
      shouldDisplayVertically ||
      !isBellowMinWidth ||
      !horizontalMinBarWidth
    ) {
      return '100%';
    }

    const barMinWidth = Math.max(
      longestGroupLabel * CHARACTER_APPROXIMATE_WIDTH_PX,
      horizontalMinBarWidth * xLevels.length,
    );

    return `${barMinWidth * xGroups.length + measurements.width}px`;
  }, [
    shouldDisplayVertically,
    isBellowMinWidth,
    horizontalMinBarWidth,
    longestGroupLabel,
    xLevels.length,
    xGroups.length,
    measurements.width,
  ]);

  const chartTheme = useMemo(() => {
    return {
      axis: {
        ...defaultTheme.axis,
        ...(theme?.axis ?? {}),
      },
    };
  }, [theme]);

  return (
    <div ref={measureRef} style={styleProps}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: chartWidth,
          height: '100%',
        }}
      >
        <ResponsiveBar
          key={chartKey}
          animate={false}
          barComponent={barCallback}
          layout={shouldDisplayVertically ? 'horizontal' : 'vertical'}
          padding={
            shouldDisplayVertically
              ? verticalGroupPaddingAsFactorOfHeight
              : (horizontalGroupPadding ?? 0.3)
          }
          innerPadding={shouldDisplayVertically ? verticalInnerPadding : 12}
          data={chartData}
          markers={
            markers ?? (shouldDisplayVertically ? undefined : chartMarkers)
          }
          keys={keys}
          defs={computedDefs}
          fill={computedFills}
          indexBy="groupId"
          margin={chartMargin}
          minValue={Math.min(...tickValues)}
          maxValue={Math.max(...tickValues)}
          {...(shouldDisplayVertically
            ? {
                gridXValues: tickValues,
                gridYValues,
              }
            : { gridYValues: tickValues })}
          groupMode="grouped"
          valueScale={{ type: 'linear' }}
          indexScale={{ type: 'band', round: true }}
          colors={colorsCb}
          borderRadius={4}
          axisTop={null}
          axisRight={null}
          axisLeft={axisLeft}
          axisBottom={axisBottom}
          enableGridX={enableGridX ?? shouldDisplayVertically}
          enableGridY={enableGridY ?? !shouldDisplayVertically}
          theme={chartTheme}
          enableLabel={false}
          role="application"
          ariaLabel="Trends bar chart"
          barAriaLabel={barAriaLabel}
          /* we need a noop function here to ignore nivo's default tooltip */
          tooltip={noop}
          layers={[
            'grid',
            'axes',
            'bars',
            'markers',
            'legends',
            ...(layers ?? []),
          ]}
          borderColor={getBorderColor}
          borderWidth={1}
        />
      </div>
    </div>
  );
};

export const cleanDefId = (id: string) => {
  // remove all characters that are not supported by Nivo on Ids
  const matches = id.match(/[A-Za-z0-9_-]/g);
  return matches ? matches.join('') : '';
};
